/**
 * InboxView (INBOX-01) — the shared office inbox. Conversations on the left
 * (Unassigned / Mine / All / Done), the thread on the right. Rides the comms
 * engine already built: inbound email threads here, unknown senders pool here.
 *
 * v1: triage (claim / done / read) + read the thread + jump to the customer to
 * reply (the consent-aware composer lives on the 360 Messages tab). Reply-in-
 * place + "make a ticket" + match-unknown-sender are the next slice.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Mail,
  MessageSquare,
  Phone,
  StickyNote,
  MessageCircle,
  CheckCircle2,
  Inbox as InboxIcon,
  Search,
  UserCheck,
  CalendarPlus,
  Sparkles,
  Loader2,
  Trash2,
  Undo2,
  UserPlus,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { useNavigateWithBack } from "@/lib/navBack";
import { useRealtimeRefresh } from "@/lib/useRealtimeRefresh";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { CreateDispatchModal } from "@/features/customer-360/CreateDispatchModal";
import { currentBurst, buildTranscript, burstScopeLabel, hasInbound } from "@/lib/conversationBurst";
import { mapUrgency, mapWindow, looksLikeWebForm, type BuilderUrgency } from "@/lib/intakeMap";
import { InlineComposer } from "./InlineComposer";
import { EmailReplyBar } from "./EmailReplyBar";
import { AssigneeMenu, Avatar, useAssignableUsers } from "./AssigneeMenu";
import { ThreadMenu } from "./ThreadMenu";
import { QualifyModal } from "./QualifyModal";
import { NewCustomerModal } from "./NewCustomerModal";
import { AttachEndpointChip } from "@/features/customer-360/AttachEndpointChip";
import { ThreadTagBar, useTags, useTagLinks, colorOf } from "./tags";
import { TOOL_BTN, TOOL_BTN_PRIMARY } from "./toolbar";
import { EmptyState } from "@/components/ui/EmptyState";
import { NewConversationLauncher } from "./NewConversationLauncher";
import { RequestsList, RequestDetail, useInboxRequests, REQUEST_KEY_PREFIX } from "./RequestsPane";
import {
  useInboxConversations,
  useConversationMessages,
  useCurrentUserEmail,
  useUpdateConversation,
  useSetMessageDeleted,
  conversationName,
  type InboxConversation,
} from "./api";

type Tab = "unassigned" | "mine" | "all" | "done" | "requests";
const TABS: { key: Tab; label: string }[] = [
  { key: "unassigned", label: "Unassigned" },
  { key: "mine", label: "Mine" },
  { key: "all", label: "All" },
  { key: "done", label: "Done" },
  // INBOX-REQUESTS-01: teammates waiting on a yes/no (time off today; time-fix
  // and schedule asks later). Only approvers see the tab.
  { key: "requests", label: "Requests" },
];

// INBOX-02 design pass (Seth 2026-08-31: "a more clear picture of email vs
// text"): every channel wears its own color + a plain-English label, on the
// row, in the header, everywhere.
const CHANNEL_META: Record<string, { Icon: React.ComponentType<{ className?: string }>; label: string; circle: string; chip: string }> = {
  email: { Icon: Mail, label: "Email", circle: "bg-sky-100 text-sky-700", chip: "bg-sky-50 text-sky-700 ring-1 ring-sky-200" },
  sms: { Icon: MessageSquare, label: "Text", circle: "bg-emerald-100 text-emerald-700", chip: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" },
  call: { Icon: Phone, label: "Call", circle: "bg-violet-100 text-violet-700", chip: "bg-violet-50 text-violet-700 ring-1 ring-violet-200" },
  note: { Icon: StickyNote, label: "Note", circle: "bg-amber-100 text-amber-700", chip: "bg-amber-50 text-amber-700 ring-1 ring-amber-200" },
  chat: { Icon: MessageCircle, label: "Chat", circle: "bg-slate-100 text-slate-600", chip: "bg-slate-50 text-slate-600 ring-1 ring-slate-200" },
};
const chMeta = (ch: string) => CHANNEL_META[ch] ?? CHANNEL_META.chat;

/** iMessage-style stamp: time today, "Yesterday", weekday this week, then date. */
function timeAgo(iso: string): string {
  const d = new Date(iso), now = new Date();
  const key = (x: Date) => `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`;
  if (key(d) === key(now)) return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const y = new Date(now); y.setDate(y.getDate() - 1);
  if (key(d) === key(y)) return "Yesterday";
  if (now.getTime() - d.getTime() < 6 * 86400000) return d.toLocaleDateString("en-US", { weekday: "short" });
  return d.toLocaleDateString("en-US", { month: "numeric", day: "numeric" });
}

export function InboxView() {
  const { data: conversations, isLoading } = useInboxConversations();
  const myEmail = useCurrentUserEmail().data ?? null;
  const update = useUpdateConversation();
  const { data: assignUsers } = useAssignableUsers();
  const userByEmail = useMemo(
    () => new Map((assignUsers ?? []).map((u) => [u.email, u])),
    [assignUsers],
  );
  const { data: tagCatalog } = useTags();
  const { data: tagLinks } = useTagLinks();
  const tagById = useMemo(() => new Map((tagCatalog ?? []).map((t) => [t.id, t])), [tagCatalog]);
  const [tagFilter, setTagFilter] = useState<Set<string>>(new Set());
  // True real-time: a webhook write to message/conversation_state refreshes
  // the inbox within ~1s — no 30s poll wait. (Tables are in the realtime
  // publication as of the inbox_live_state_mirror migration.)
  useRealtimeRefresh(
    ["message", "conversation_state"],
    [["inbox_conversations"], ["conversation_messages"]],
  );
  // Tab + selected conversation live in the URL so leaving for a customer 360
  // and coming back (the Back button OR browser back) restores your exact spot —
  // useNavigateWithBack records the full URL, query params included.
  const [params, setParams] = useSearchParams();
  const { canApprove, requests } = useInboxRequests();
  const visibleTabs = canApprove ? TABS : TABS.filter((t) => t.key !== "requests");
  const tabParam = params.get("tab");
  const tab: Tab = visibleTabs.some((t) => t.key === tabParam) ? (tabParam as Tab) : "unassigned";
  const selectedKey = params.get("c");
  const [search, setSearch] = useState("");
  const patchParams = useCallback(
    (next: Record<string, string | null>) => {
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(next)) {
            if (v == null || v === "") p.delete(k);
            else p.set(k, v);
          }
          return p;
        },
        { replace: true },
      );
    },
    [setParams],
  );
  const setTab = (t: Tab) => patchParams({ tab: t });
  const setSelectedKey = (key: string | null) => patchParams({ c: key });

  const all = conversations ?? [];
  const counts = useMemo(() => {
    const open = all.filter((c) => c.conv_status !== "done");
    return {
      unassigned: open.filter((c) => !c.assignee).length,
      mine: open.filter((c) => c.assignee === myEmail).length,
      all: open.length,
      done: all.filter((c) => c.conv_status === "done").length,
      requests: requests.length,
    };
  }, [all, myEmail, requests.length]);

  const list = useMemo(() => {
    const open = all.filter((c) => c.conv_status !== "done");
    let rows =
      tab === "unassigned"
        ? open.filter((c) => !c.assignee)
        : tab === "mine"
          ? open.filter((c) => c.assignee === myEmail)
          : tab === "done"
            ? all.filter((c) => c.conv_status === "done")
            : open;
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((c) =>
        `${conversationName(c)} ${c.endpoint ?? ""} ${c.subject ?? ""} ${c.body ?? ""}`
          .toLowerCase()
          .includes(q),
      );
    }
    if (tagFilter.size) {
      rows = rows.filter((c) =>
        (tagLinks?.[c.conversation_key] ?? []).some((id) => tagFilter.has(id)),
      );
    }
    return rows;
  }, [all, tab, myEmail, search, tagFilter, tagLinks]);

  const selected = all.find((c) => c.conversation_key === selectedKey) ?? null;
  const selectedRequest =
    selectedKey?.startsWith(REQUEST_KEY_PREFIX)
      ? requests.find((r) => REQUEST_KEY_PREFIX + r.id === selectedKey) ?? null
      : null;
  // INBOX-COMPOSE-01: a thread started from the "+" is claimed as yours and
  // opened, so the conversation you just began is the one on screen.
  const onStarted = (custno: string) => {
    update.mutate({ conversation_key: custno, custno, endpoint: null, patch: { status: "open", assignee: myEmail } });
    patchParams({ tab: "mine", c: custno });
  };

  const openConversation = (c: InboxConversation) => {
    setSelectedKey(c.conversation_key);
    if (c.unread) {
      update.mutate({
        conversation_key: c.conversation_key,
        custno: c.custno,
        endpoint: c.endpoint,
        patch: { last_read_at: new Date().toISOString() },
      });
    }
  };

  const unreadOpen = useMemo(() => all.filter((c) => c.unread && c.conv_status !== "done").length, [all]);

  return (
    // h-full: the shell's <main> is the scroll container — sizing to it (not
    // to a hard-coded 100vh minus a guessed header height) means no stray
    // page scrollbar when the top bar changes height (INBOX-HD, 2026-09-02).
    <div className="flex h-full overflow-hidden">
      {/* Conversation list — the Front / HubSpot split: a calm column with the
          filters up top, one row per conversation, the thread on the right. */}
      <aside className="flex w-[24rem] shrink-0 flex-col border-r border-hairline bg-white">
        <div className="border-b border-hairline px-4 pb-3 pt-4">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-100">
              <InboxIcon className="h-[18px] w-[18px]" strokeWidth={2} />
            </span>
            <h2 className="text-[22px] font-extrabold leading-none tracking-tight text-slate-900">Inbox</h2>
            {unreadOpen > 0 && (
              <span className="rounded-full bg-red-600 px-2 py-0.5 text-[11px] font-bold tabular-nums text-white" title={`${unreadOpen} unread`}>
                {unreadOpen}
              </span>
            )}
            <NewConversationLauncher onStarted={onStarted} />
          </div>
          {/* iOS segmented control */}
          <div className="mt-3 flex gap-0.5 rounded-full bg-slate-100 p-0.5">
            {visibleTabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={cn(
                  "flex-1 rounded-full py-1.5 text-[12.5px] font-bold transition-all",
                  tab === t.key
                    ? "bg-white text-slate-900 shadow-[0_1px_4px_rgba(0,0,0,0.12)]"
                    : "text-slate-500 hover:text-slate-700",
                )}
              >
                {t.label}
                <span className={cn("ml-1 tabular-nums font-semibold", t.key === "requests" && counts.requests > 0 ? "text-amber-600" : "text-slate-400")}>
                  {counts[t.key]}
                </span>
              </button>
            ))}
          </div>
          <div className="relative mt-2.5">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search conversations…"
              className="h-9 w-full rounded-full border border-transparent bg-slate-100/80 pl-10 pr-3 text-[13px] text-slate-900 placeholder:text-slate-500 transition-all duration-150 ease-out-quart hover:bg-slate-100 focus:border-brand-500/60 focus:bg-white focus:outline-none focus:ring-4 focus:ring-brand-500/15"
            />
          </div>
          {(tagCatalog ?? []).length > 0 && (
            <div className="mt-2.5 flex flex-wrap gap-1">
              {(tagCatalog ?? []).map((t) => {
                const on = tagFilter.has(t.id);
                const c = colorOf(t.color);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() =>
                      setTagFilter((prev) => {
                        const n = new Set(prev);
                        if (n.has(t.id)) n.delete(t.id);
                        else n.add(t.id);
                        return n;
                      })
                    }
                    className={cn(
                      "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold transition-colors",
                      on ? c.chip : "bg-slate-50 text-slate-500 ring-1 ring-inset ring-slate-200/70 hover:bg-slate-100 hover:text-slate-700",
                    )}
                    title={on ? `Filtering by ${t.name}` : `Filter by ${t.name}`}
                  >
                    <span className={cn("h-1.5 w-1.5 rounded-full", c.dot)} /> {t.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {tab === "requests" ? (
            <RequestsList requests={requests} selectedKey={selectedKey} onOpen={(key) => setSelectedKey(key)} />
          ) : isLoading ? (
            <div className="space-y-2 p-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-16 animate-pulse rounded-xl bg-slate-100" />
              ))}
            </div>
          ) : list.length === 0 ? (
            <EmptyState
              size="sm"
              className="py-14"
              icon={search.trim() || tagFilter.size ? Search : tab === "done" ? InboxIcon : CheckCircle2}
              tone={search.trim() || tagFilter.size || tab === "done" ? "slate" : "emerald"}
              title={tab === "done" ? "Nothing closed yet" : search.trim() || tagFilter.size ? "No matches" : tab === "mine" ? "Nothing assigned to you" : "Nothing waiting"}
              hint={
                tab === "done"
                  ? "Conversations you mark Done land here."
                  : search.trim() || tagFilter.size
                    ? "Try a different search or clear the tag filter."
                    : "New texts and emails appear here as they arrive."
              }
            />
          ) : (
            <ul>
              {list.map((c) => {
                const meta = chMeta(c.channel);
                const Icon = meta.Icon;
                const active = c.conversation_key === selectedKey;
                return (
                  <li key={c.conversation_key}>
                    <button
                      type="button"
                      onClick={() => openConversation(c)}
                      aria-current={active ? "true" : undefined}
                      className={cn(
                        "relative flex w-full items-start gap-3 border-b border-hairline px-3.5 py-3 text-left transition-colors",
                        // Selected row: tinted + a 3px brand bar on the left edge
                        // (Front / Linear) so the eye finds it without reading.
                        active
                          ? "bg-brand-50/70 before:absolute before:inset-y-2 before:left-0 before:w-[3px] before:rounded-r-full before:bg-brand-600"
                          : "hover:bg-slate-50",
                      )}
                    >
                      <span className="relative mt-0.5 shrink-0">
                        <span className={cn("flex h-10 w-10 items-center justify-center rounded-full", meta.circle)}>
                          <Icon className="h-4.5 w-4.5" />
                        </span>
                        {c.unread && (
                          <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white ring-2 ring-white">
                            {c.unread_count > 9 ? "9+" : c.unread_count || 1}
                          </span>
                        )}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-baseline gap-1.5">
                          <span
                            className={cn(
                              "truncate text-[14px]",
                              c.unread
                                ? "font-extrabold text-slate-900"
                                : "font-semibold text-slate-700",
                            )}
                          >
                            {conversationName(c)}
                          </span>
                          <span className="ml-auto shrink-0 text-[11px] tabular-nums text-slate-400">
                            {timeAgo(c.last_at)}
                          </span>
                        </span>
                        <span
                          className={cn(
                            "mt-0.5 block truncate text-[12.5px] leading-snug",
                            c.unread ? "font-medium text-slate-600" : "text-slate-400",
                          )}
                        >
                          {c.subject || c.body || "—"}
                        </span>
                        <span className="mt-1.5 flex flex-wrap items-center gap-1">
                          <span className={cn("rounded-full px-1.5 py-px text-[10.5px] font-bold", meta.chip)}>
                            {meta.label}
                          </span>
                          {(tagLinks?.[c.conversation_key] ?? []).slice(0, 3).map((id) => {
                            const t = tagById.get(id);
                            if (!t) return null;
                            const tc = colorOf(t.color);
                            return (
                              <span key={id} className={cn("rounded-full px-1.5 py-px text-[10.5px] font-semibold", tc.chip)}>
                                {t.name}
                              </span>
                            );
                          })}
                          {!c.custno &&
                            (/@ceas\.us$/i.test(c.endpoint ?? "") ? (
                              // Known operational sender, not a mystery — the
                              // after-hours answering service's 7 AM sheet.
                              <span className="rounded-full bg-sky-50 px-1.5 py-px text-[10.5px] font-semibold text-sky-700">
                                Answering service
                              </span>
                            ) : (
                              // We may know the NAME (Podium's contact) —
                              // what's missing is the link to a customer
                              // account, so say exactly that.
                              <span className="rounded-full bg-amber-50 px-1.5 py-px text-[10.5px] font-semibold text-amber-700">
                                No customer match
                              </span>
                            ))}
                          {c.assignee && c.assignee === myEmail && (
                            <span className="rounded-full bg-brand-50 px-1.5 py-px text-[10.5px] font-semibold text-brand-700">
                              Mine
                            </span>
                          )}
                          {c.assignee && c.assignee !== myEmail && (
                            <span className="ml-auto flex shrink-0 items-center gap-1 text-[11px] text-slate-400">
                              <Avatar
                                name={userByEmail.get(c.assignee)?.name ?? c.assignee}
                                email={c.assignee}
                                size="xs"
                              />
                              <span className="max-w-[64px] truncate">
                                {userByEmail.get(c.assignee)?.first ?? c.assignee.split("@")[0]}
                              </span>
                            </span>
                          )}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </aside>

      {/* Thread */}
      <main className="min-w-0 flex-1 bg-slate-50/40">
        {selectedRequest ? (
          <RequestDetail key={selectedRequest.id} request={selectedRequest} onDecided={() => setSelectedKey(null)} />
        ) : selected ? (
          <ThreadPane
            key={selected.conversation_key}
            conversation={selected}
            myEmail={myEmail}
            onAssign={(assignee) =>
              update.mutate({
                conversation_key: selected.conversation_key,
                custno: selected.custno,
                endpoint: selected.endpoint,
                patch: { assignee },
                provider_conversation_id: selected.provider_conversation_id,
              })
            }
            onStatus={(status) =>
              update.mutate({
                conversation_key: selected.conversation_key,
                custno: selected.custno,
                endpoint: selected.endpoint,
                patch: { status },
                provider_conversation_id: selected.provider_conversation_id,
              })
            }
            onQualified={(custno) => setSelectedKey(custno)}
            writeBackError={update.isError ? String((update.error as Error)?.message ?? update.error) : null}
          />
        ) : (
          <div className="flex h-full flex-col items-center justify-center">
            <EmptyState
              icon={tab === "requests" ? UserCheck : InboxIcon}
              title={tab === "requests" ? "Pick a request" : "Pick a conversation"}
              hint={
                tab === "requests"
                  ? "Approve or deny it here."
                  : "Read, reply, claim, or turn it into a visit."
              }
            />
          </div>
        )}
      </main>
    </div>
  );
}

function ThreadPane({
  conversation,
  myEmail,
  onAssign,
  onStatus,
  onQualified,
  writeBackError,
}: {
  conversation: InboxConversation;
  myEmail: string | null;
  onAssign: (assignee: string | null) => void;
  onStatus: (status: "open" | "done") => void;
  onQualified: (custno: string) => void;
  writeBackError?: string | null;
}) {
  const navigate = useNavigateWithBack();
  const canSend = useCan("comms.send");
  const [qualifying, setQualifying] = useState(false);
  const [newCust, setNewCust] = useState(false);
  const { data: messages, isLoading } = useConversationMessages(
    conversation.custno,
    conversation.endpoint,
  );
  const messagesRef = useRef<HTMLDivElement>(null);
  // Open at the BOTTOM — newest messages first to the eye, like any chat app
  // (Seth 2026-06-17). Re-runs when the thread changes or a message arrives.
  useEffect(() => {
    const el = messagesRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [messages, conversation.conversation_key]);
  const isDone = conversation.conv_status === "done";
  // Podium's contact name when it meaningfully differs from the account name
  // ("Bill Lindquist" texting on the "William Lindquist" account).
  const accountName = conversationName(conversation);
  const textsAs =
    conversation.provider_contact_name &&
    conversation.custno &&
    conversation.provider_contact_name.trim().toLowerCase() !==
      accountName.trim().toLowerCase()
      ? conversation.provider_contact_name.trim()
      : null;

  // Create-dispatch from the inbox — the same flow as the 360 Messages tab
  // (Seth 2026-06-25: "Shouldn't I be able to create a dispatch from anywhere").
  // pendingDispatch opens the modal; "Summarize → dispatch" AI-summarizes the
  // WHOLE current back-and-forth (not one message — Seth 2026-06-26) into the
  // editable notes first (propose-style — nothing books until saved).
  const custno = conversation.custno;
  const [pendingDispatch, setPendingDispatch] = useState<{
    initialDescription?: string;
    sourceCaption?: string;
    prefillUrgency?: BuilderUrgency | null;
    prefillWindow?: string | null;
    prefillTypeName?: string | null;
  } | null>(null);
  const [summarizingId, setSummarizingId] = useState<string | null>(null);
  // After a dispatch is booked from this conversation, offer to mark it Done —
  // which syncs to Podium (close the loop, Seth 2026-06-26). Not silent.
  const [bookedDone, setBookedDone] = useState(false);
  // Reversible per-message cleanup (Seth 2026-06-26) — soft-delete + an 8s Undo.
  const delMsg = useSetMessageDeleted();
  const [undoMsgId, setUndoMsgId] = useState<string | null>(null);
  const deleteMessage = (id: string) => {
    delMsg.mutate({ id, deleted: true });
    setUndoMsgId(id);
    window.setTimeout(() => setUndoMsgId((cur) => (cur === id ? null : cur)), 8000);
  };
  const msgDelBtn = (id: string) => (
    <button
      type="button"
      onClick={() => deleteMessage(id)}
      title="Delete this message (you can undo)"
      aria-label="Delete message"
      className="shrink-0 rounded p-1 text-slate-300 opacity-0 transition-all hover:bg-red-50 hover:text-red-500 group-hover:opacity-100"
    >
      <Trash2 className="h-3.5 w-3.5" />
    </button>
  );
  const { data: prefill } = useQuery({
    queryKey: ["inbox_dispatch_prefill", custno],
    enabled: !!custno && !!pendingDispatch,
    queryFn: async () => {
      const [c, l] = await Promise.all([
        supabase.from("mirror_customer").select("fullname").eq("custno", custno!).maybeSingle(),
        supabase
          .from("mirror_location")
          .select("locno, add1, city, phone1")
          .eq("custno", custno!)
          .order("locno")
          .limit(1)
          .maybeSingle(),
      ]);
      const loc = (l.data ?? {}) as {
        locno?: string;
        add1?: string | null;
        city?: string | null;
        phone1?: string | null;
      };
      return {
        name:
          (((c.data?.fullname as string | undefined) ?? "").replace(/\s+\d{4,}$/, "")) ||
          accountName,
        locno: loc.locno ?? "00001",
        address: loc.add1 ?? null,
        city: loc.city ?? null,
        phone: loc.phone1 ?? null,
      };
    },
  });
  // The current back-and-forth (both sides, gap-scoped) — what the AI reads.
  const burst = useMemo(() => currentBurst(messages ?? []), [messages]);
  const canSummarize = !!custno && hasInbound(burst);
  // Shared intake: text → summarize-intake → open the builder prefilled with the
  // summary (notes) AND the structured fields (urgency, window) on the controls.
  const runIntake = async (text: string, caption: string, id: string) => {
    setSummarizingId(id);
    try {
      const { data } = await supabase.functions.invoke("summarize-intake", { body: { text } });
      const d = data as { ok?: boolean; summary?: string; fields?: { urgency?: string; promised_window?: string; suggested_service_type?: string } } | null;
      setPendingDispatch({
        initialDescription: d?.ok && d.summary ? d.summary : text,
        sourceCaption: caption,
        prefillUrgency: mapUrgency(d?.fields?.urgency),
        prefillWindow: mapWindow(d?.fields?.promised_window),
        prefillTypeName: d?.fields?.suggested_service_type || null,
      });
    } catch {
      setPendingDispatch({ initialDescription: text, sourceCaption: caption });
    } finally {
      setSummarizingId(null);
    }
  };
  const summarizeConversation = () =>
    runIntake(buildTranscript(burst), `AI summary of ${burstScopeLabel(burst)}`, "burst");

  return (
    <div className="flex h-full flex-col">
      {/* header */}
      <div className="flex items-center justify-between gap-3 border-b border-hairline bg-white px-5 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className={cn("flex h-11 w-11 shrink-0 items-center justify-center rounded-full", chMeta(conversation.channel).circle)}>
            {(() => { const I = chMeta(conversation.channel).Icon; return <I className="h-5 w-5" />; })()}
          </span>
          <div className="min-w-0">
          <div className="truncate text-[17px] font-extrabold tracking-tight text-slate-900">
            {conversation.custno ? (
              <button
                type="button"
                onClick={() => navigate(`/customers/${conversation.custno}`)}
                title="Open the customer record"
                className="rounded transition-colors hover:text-brand-700 hover:underline"
              >
                {accountName}
              </button>
            ) : (
              accountName
            )}
            {textsAs && (
              <span className="ml-2 text-[12.5px] font-normal text-slate-500">
                · texts as {textsAs}
              </span>
            )}
          </div>
          <div className="mt-0.5 flex min-w-0 items-center gap-1.5 truncate text-[12.5px] text-slate-500">
            <span className={cn("rounded-full px-1.5 py-px text-[10.5px] font-bold", chMeta(conversation.channel).chip)}>
              {chMeta(conversation.channel).label}
            </span>
            <span className="truncate">
              {conversation.endpoint}
              {!conversation.custno ? " · not matched to a customer" : ""}
            </span>
            {/* ATTACH-01: matched conversation, but this number/email isn't on
                the account → one tap puts it there (then Pegasus recognizes
                it next time). Renders nothing when already known. */}
            {conversation.custno && conversation.endpoint && (
              <AttachEndpointChip
                custno={conversation.custno}
                endpoint={conversation.endpoint}
                name={conversation.provider_contact_name ?? accountName}
              />
            )}
          </div>
          <div className="mt-1.5">
            <ThreadTagBar conversationKey={conversation.conversation_key} />
          </div>
          </div>
        </div>
        {/* Toolbar — pill buttons at one height (the shell's h-8 secondary
            size), primary actions filled, the rest outlined. */}
        <div className="flex shrink-0 items-center gap-1.5">
          {!conversation.custno && (
            <button
              type="button"
              onClick={() => setQualifying(true)}
              title="Link this conversation to a customer record"
              className={TOOL_BTN_PRIMARY}
            >
              <UserCheck className="h-3.5 w-3.5" />
              Qualify
            </button>
          )}
          {!conversation.custno && (
            <button
              type="button"
              onClick={() => setNewCust(true)}
              title="Create a brand-new customer from this lead, then book their first visit"
              className={TOOL_BTN}
            >
              <UserPlus className="h-3.5 w-3.5 text-brand-600" />
              New customer
            </button>
          )}
          {canSummarize && (
            <button
              type="button"
              onClick={summarizeConversation}
              disabled={summarizingId === "burst"}
              title="AI reads the recent back-and-forth and fills the dispatch"
              className={TOOL_BTN_PRIMARY}
            >
              {summarizingId === "burst" ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="h-3.5 w-3.5" />
              )}
              {summarizingId === "burst" ? "Reading…" : "Dispatch from conversation"}
            </button>
          )}
          {conversation.custno && (
            <button
              type="button"
              onClick={() => setPendingDispatch({})}
              title="Create a dispatch for this customer"
              className={TOOL_BTN}
            >
              <CalendarPlus className="h-3.5 w-3.5 text-brand-600" />
              Create dispatch
            </button>
          )}
          <AssigneeMenu assignee={conversation.assignee} onAssign={onAssign} myEmail={myEmail} />
          <button
            type="button"
            onClick={() => onStatus(isDone ? "open" : "done")}
            className={cn(
              "inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-[12.5px] font-semibold transition-colors",
              isDone
                ? "border border-hairline bg-white text-slate-600 hover:bg-slate-50"
                : "bg-emerald-600 text-white shadow-e1 hover:bg-emerald-700",
            )}
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            {isDone ? "Reopen" : "Done"}
          </button>
          <ThreadMenu conversation={conversation} />
        </div>
      </div>

      {/* write-back problem (local change saved; Podium disagreed) */}
      {writeBackError && (
        <div className="border-b border-amber-200 bg-amber-50 px-5 py-2 text-xs text-amber-800">
          {writeBackError}
        </div>
      )}

      {/* Close the loop: a dispatch was just booked from this conversation. */}
      {bookedDone && (
        <div className="flex items-center justify-between gap-3 border-b border-emerald-200 bg-emerald-50 px-5 py-2.5 text-xs text-emerald-900">
          <span className="font-medium">
            Dispatch created. Mark this request done? It'll close here and in Podium.
          </span>
          <span className="flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              onClick={() => { onStatus("done"); setBookedDone(false); }}
              className="rounded-md bg-emerald-600 px-2.5 py-1 font-semibold text-white transition-colors hover:bg-emerald-700"
            >
              Mark done
            </button>
            <button
              type="button"
              onClick={() => setBookedDone(false)}
              className="rounded-md border border-emerald-300 bg-white px-2.5 py-1 font-medium text-emerald-800 transition-colors hover:bg-emerald-100"
            >
              Keep open
            </button>
          </span>
        </div>
      )}

      {/* Reversible message cleanup — an 8s Undo after a soft-delete. */}
      {undoMsgId && (
        <div className="flex items-center justify-between gap-3 border-b border-slate-700 bg-slate-800 px-5 py-2 text-xs text-white">
          <span>Message deleted.</span>
          <button
            type="button"
            onClick={() => { delMsg.mutate({ id: undoMsgId, deleted: false }); setUndoMsgId(null); }}
            className="inline-flex items-center gap-1 rounded-md bg-white/10 px-2 py-1 font-semibold transition-colors hover:bg-white/20"
          >
            <Undo2 className="h-3.5 w-3.5" /> Undo
          </button>
        </div>
      )}

      {/* messages */}
      <div ref={messagesRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4">
        {isLoading ? (
          <div className="space-y-3">
            {[0, 1].map((i) => (
              <div key={i} className="h-16 animate-pulse rounded-lg bg-slate-100" />
            ))}
          </div>
        ) : (
          (messages ?? []).map((m) => {
            const inbound = m.direction === "inbound";
            const note = m.channel === "note";
            if (note) {
              return (
                <div key={m.id} className="group mx-auto flex max-w-2xl items-start gap-1">
                  <div className="flex-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    <span className="font-semibold">Internal note</span> · {m.body}
                  </div>
                  {msgDelBtn(m.id)}
                </div>
              );
            }
            return (
              <div
                key={m.id}
                className={cn("group flex items-center gap-1", inbound ? "justify-start" : "justify-end")}
              >
                {!inbound && msgDelBtn(m.id)}
                <div
                  className={cn(
                    "max-w-[72%] rounded-2xl px-4 py-2.5 text-[14.5px] leading-relaxed shadow-e1",
                    inbound
                      ? "rounded-bl-md bg-white text-slate-800 ring-1 ring-black/[0.05]"
                      : "rounded-br-md bg-brand-600 text-white",
                  )}
                >
                  {m.subject && (
                    <div
                      className={cn(
                        "mb-0.5 text-xs font-semibold",
                        inbound ? "text-slate-900" : "text-white",
                      )}
                    >
                      {m.subject}
                    </div>
                  )}
                  <div className="whitespace-pre-wrap leading-snug">
                    {m.body || "—"}
                  </div>
                  <div
                    className={cn(
                      "mt-1 text-[11px]",
                      inbound ? "text-slate-400" : "text-brand-100",
                    )}
                  >
                    {inbound ? "Received" : "Sent"} ·{" "}
                    {new Date(m.created_at).toLocaleString("en-US", {
                      month: "short",
                      day: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                    {m.attachments && m.attachments.length > 0
                      ? ` · 📎 ${m.attachments.length}`
                      : ""}
                  </div>
                  {inbound && custno && looksLikeWebForm(m.body) && (
                    <button
                      type="button"
                      onClick={() => runIntake(m.body ?? "", "AI-filled from the web request", m.id)}
                      disabled={summarizingId === m.id}
                      title="AI reads this web request and fills the dispatch"
                      className="mt-1.5 inline-flex items-center gap-1 rounded-md border border-brand-200 bg-brand-50 px-2 py-1 text-[11px] font-semibold text-brand-700 transition-colors hover:bg-brand-100 disabled:opacity-60"
                    >
                      {summarizingId === m.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <CalendarPlus className="h-3 w-3" />
                      )}
                      {summarizingId === m.id ? "Reading the request…" : "Make a dispatch"}
                    </button>
                  )}
                </div>
                {inbound && msgDelBtn(m.id)}
              </div>
            );
          })
        )}
      </div>

      {/* Docked composer (Podium-style) — reply right here on the thread. SMS
          and email both get the same feel; email adds a subject line. */}
      {conversation.custno && canSend && conversation.channel === "sms" && (
        <InlineComposer
          custno={conversation.custno}
          endpoint={conversation.endpoint}
          name={accountName}
          providerName={conversation.provider_contact_name}
        />
      )}
      {conversation.custno && canSend && conversation.channel === "email" && (
        <EmailReplyBar
          custno={conversation.custno}
          endpoint={conversation.endpoint}
          name={accountName}
          providerName={conversation.provider_contact_name}
          threadSubject={conversation.subject}
        />
      )}

      {/* Qualify — link an unmatched conversation to a customer record. */}
      {qualifying && (
        <QualifyModal
          conversation={conversation}
          myEmail={myEmail}
          onClose={() => setQualifying(false)}
          onQualified={onQualified}
        />
      )}

      {/* New customer — create a brand-new Pegasus customer from this lead + book. */}
      {newCust && (
        <NewCustomerModal
          conversation={conversation}
          onClose={() => setNewCust(false)}
        />
      )}

      {/* Create a dispatch right from the inbox (Seth 2026-06-25) — same modal
          the 360 uses, prefilled with this customer; the AI summary (if used)
          lands in the editable notes. */}
      {pendingDispatch && prefill && custno && (
        <CreateDispatchModal
          custno={custno}
          locno={prefill.locno}
          customerName={prefill.name}
          address={prefill.address}
          city={prefill.city}
          phone={prefill.phone}
          initialDescription={pendingDispatch.initialDescription ?? ""}
          sourceCaption={pendingDispatch.sourceCaption ?? null}
          prefillUrgency={pendingDispatch.prefillUrgency ?? null}
          prefillWindow={pendingDispatch.prefillWindow ?? null}
          prefillTypeName={pendingDispatch.prefillTypeName ?? null}
          onCreated={() => !isDone && setBookedDone(true)}
          onClose={() => setPendingDispatch(null)}
        />
      )}
    </div>
  );
}
